import {
  useRef,
  useEffect,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import WaveSurfer from "wavesurfer.js";
import RegionsPlugin, { Region } from "wavesurfer.js/dist/plugins/regions.js";
import {
  PlayIcon,
  PauseIcon,
  StopIcon,
  TrashIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  BackwardIcon,
  ForwardIcon,
} from "@heroicons/react/24/solid";
import IconButton from "../common/IconButton";

interface AudioWaveformEditorProps {
  audioData: {
    channels: Float32Array[];
    sampleRate: number;
  } | null;
  version: number;
  onDeleteRegion: (start: number, end: number) => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  duration: number;
  isRecording: boolean;
  /** External container element for wavesurfer to render into */
  containerElement?: HTMLDivElement | null;
  /** Called when user clicks/seeks to a position in the waveform */
  onSeek?: (time: number) => void;
  /** Called when user drags a selected region to a new position */
  onMoveRegion?: (startTime: number, endTime: number, toTime: number) => void;
}

export interface AudioWaveformEditorHandle {
  getCurrentTime: () => number;
}

const AudioWaveformEditor = forwardRef<
  AudioWaveformEditorHandle,
  AudioWaveformEditorProps
>(function AudioWaveformEditor(
  {
    audioData,
    version,
    onDeleteRegion,
    onUndo,
    onRedo,
    canUndo,
    canRedo,
    duration,
    isRecording,
    containerElement,
    onSeek,
    onMoveRegion,
  },
  ref
) {
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const regionsRef = useRef<RegionsPlugin | null>(null);
  const activeRegionRef = useRef<Region | null>(null);
  const zoomRef = useRef(50);
  const cursorTimeRef = useRef(0);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [zoom, setZoom] = useState(50);
  const [hasRegion, setHasRegion] = useState(false);

  useImperativeHandle(ref, () => ({
    getCurrentTime: () => cursorTimeRef.current,
  }));

  // Initialize / reinitialize wavesurfer when audioData or container changes
  useEffect(() => {
    if (!containerElement || !audioData) {
      setCurrentTime(0);
      cursorTimeRef.current = 0;
      setIsPlaying(false);
      setHasRegion(false);
      return;
    }

    if (wavesurferRef.current) {
      wavesurferRef.current.destroy();
      wavesurferRef.current = null;
      regionsRef.current = null;
      activeRegionRef.current = null;
      setHasRegion(false);
    }

    const regions = RegionsPlugin.create();
    regionsRef.current = regions;

    // Resolve theme-aware neutrals (canvas colors can't use CSS var() strings)
    const rootStyle = getComputedStyle(document.documentElement);
    const ws = WaveSurfer.create({
      container: containerElement,
      waveColor: `oklch(${rootStyle.getPropertyValue("--b3")})`,
      progressColor: "#0768fd",
      cursorColor: `oklch(${rootStyle.getPropertyValue("--bc")})`,
      cursorWidth: 2,
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      height: 180,
      normalize: true,
      plugins: [regions],
    });

    ws.loadBlob(
      new Blob(
        [createWavBuffer(audioData.channels, audioData.sampleRate)],
        { type: "audio/wav" }
      )
    );

    regions.enableDragSelection({
      color: "rgba(239, 68, 68, 0.25)",
    });

    const regionOriginalPos = { start: 0, end: 0 };
    let dropIndicator: HTMLDivElement | null = null;
    let isDraggingRegion = false;

    regions.on("region-created", (region: Region) => {
      if (
        activeRegionRef.current &&
        activeRegionRef.current.id !== region.id
      ) {
        activeRegionRef.current.remove();
      }
      activeRegionRef.current = region;
      regionOriginalPos.start = region.start;
      regionOriginalPos.end = region.end;
      setHasRegion(true);

      // Listen for drag on this region to show drop indicator
      region.on("update", (side) => {
        if (side) return; // resize, not drag
        const origDuration = regionOriginalPos.end - regionOriginalPos.start;
        const newDuration = region.end - region.start;
        if (Math.abs(origDuration - newDuration) > 0.01) return; // resize

        if (!isDraggingRegion) {
          isDraggingRegion = true;
          // Set grabbing cursor on the whole document during drag
          document.body.style.cursor = "grabbing";
          // Snap the region back to its original position visually
          // and create a drop indicator line instead
          const shadowRoot = containerElement?.querySelector("div")?.shadowRoot;
          const wrapper = shadowRoot?.querySelector(".wrapper") as HTMLElement;
          if (wrapper && !dropIndicator) {
            dropIndicator = document.createElement("div");
            dropIndicator.style.cssText = "position:absolute;top:0;width:2px;height:100%;background:#3b82f6;z-index:10;pointer-events:none;";
            wrapper.appendChild(dropIndicator);
          }
        }

        // Update drop indicator position
        if (dropIndicator) {
          const duration = ws.getDuration();
          const dropPct = (region.start / duration) * 100;
          dropIndicator.style.left = `${dropPct}%`;
        }

        // Snap the region element back to original position
        const duration = ws.getDuration();
        const el = (region as any).element as HTMLElement | undefined;
        if (el) {
          el.style.left = `${(regionOriginalPos.start / duration) * 100}%`;
          el.style.right = `${((duration - regionOriginalPos.end) / duration) * 100}%`;
        }
      });
    });

    regions.on("region-updated", (region: Region) => {
      if (!isDraggingRegion) {
        // Resize — update stored position
        regionOriginalPos.start = region.start;
        regionOriginalPos.end = region.end;
        return;
      }

      isDraggingRegion = false;
      document.body.style.cursor = "";
      const dropTime = region.start;

      // Clean up drop indicator
      if (dropIndicator) {
        dropIndicator.remove();
        dropIndicator = null;
      }

      // Reset region to original position
      region.setOptions({ start: regionOriginalPos.start, end: regionOriginalPos.end });

      // Only move if dropped outside the original region
      if (dropTime < regionOriginalPos.start - 0.01 || dropTime > regionOriginalPos.end + 0.01) {
        onMoveRegion?.(regionOriginalPos.start, regionOriginalPos.end, dropTime);
      }
    });

    const shadowHost = containerElement?.querySelector("div");
    const setBlink = (blink: boolean) => {
      if (blink) shadowHost?.classList.add("ap-cursor-blink");
      else shadowHost?.classList.remove("ap-cursor-blink");
    };

    ws.on("play", () => { setIsPlaying(true); setBlink(false); });
    ws.on("pause", () => { setIsPlaying(false); setBlink(true); });
    ws.on("finish", () => { setIsPlaying(false); setBlink(true); });
    ws.on("timeupdate", (time: number) => {
      setCurrentTime(time);
      cursorTimeRef.current = time;
    });
    ws.on("click", () => {
      // Single click on waveform clears any selected region
      if (activeRegionRef.current) {
        activeRegionRef.current.remove();
        activeRegionRef.current = null;
        setHasRegion(false);
      }
    });
    ws.on("seeking", (time: number) => {
      cursorTimeRef.current = time;
      onSeek?.(time);
    });

    ws.on("ready", () => {
      ws.zoom(zoomRef.current);

      // Start cursor at the end so "Record at Cursor" appends by default
      const dur = ws.getDuration();
      ws.seekTo(1);
      setCurrentTime(dur);
      cursorTimeRef.current = dur;

      // Inject blinking cursor animation into WaveSurfer's shadow DOM
      setBlink(true);
      const shadowRoot = containerElement?.querySelector("div")?.shadowRoot;
      if (shadowRoot && !shadowRoot.querySelector("#ap-cursor-blink-style")) {
        const style = document.createElement("style");
        style.id = "ap-cursor-blink-style";
        style.textContent = `
          @keyframes ap-cursor-blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0; }
          }
          :host(.ap-cursor-blink) .cursor { animation: ap-cursor-blink 1.2s ease-in-out infinite; }
          :host(:not(.ap-cursor-blink)) .cursor { animation: none; }
        `;
        shadowRoot.appendChild(style);
      }
    });

    wavesurferRef.current = ws;

    return () => {
      ws.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioData, version, containerElement]);

  const handleGoToBeginning = useCallback(() => {
    const ws = wavesurferRef.current;
    if (ws) {
      ws.seekTo(0);
      setCurrentTime(0);
      cursorTimeRef.current = 0;
    }
  }, []);

  const handleGoToEnd = useCallback(() => {
    const ws = wavesurferRef.current;
    if (ws) {
      const dur = ws.getDuration();
      ws.seekTo(1);
      setCurrentTime(dur);
      cursorTimeRef.current = dur;
    }
  }, []);

  const handlePlayPause = useCallback(() => {
    wavesurferRef.current?.playPause();
  }, []);

  const handleStop = useCallback(() => {
    const ws = wavesurferRef.current;
    if (ws) {
      ws.stop();
      setIsPlaying(false);
      setCurrentTime(0);
      cursorTimeRef.current = 0;
    }
  }, []);

  const handleDeleteSelection = useCallback(() => {
    const region = activeRegionRef.current;
    if (!region) return;

    const start = region.start;
    const end = region.end;
    const el = region.element;

    wavesurferRef.current?.stop();
    setIsPlaying(false);
    activeRegionRef.current = null;
    setHasRegion(false);

    if (el) {
      el.style.transition =
        "transform 0.25s ease-in-out, opacity 0.2s ease-in";
      el.style.transformOrigin = "center";
      el.style.transform = "scaleX(0)";
      el.style.opacity = "0";
      setTimeout(() => onDeleteRegion(start, end), 250);
    } else {
      onDeleteRegion(start, end);
    }
  }, [onDeleteRegion]);

  const handleZoomIn = useCallback(() => {
    const newZoom = Math.min(zoom + 25, 500);
    setZoom(newZoom);
    zoomRef.current = newZoom;
    wavesurferRef.current?.zoom(newZoom);
  }, [zoom]);

  const handleZoomOut = useCallback(() => {
    const newZoom = Math.max(zoom - 25, 10);
    setZoom(newZoom);
    zoomRef.current = newZoom;
    wavesurferRef.current?.zoom(newZoom);
  }, [zoom]);

  const handleZoomSlider = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newZoom = Number(e.target.value);
      setZoom(newZoom);
      zoomRef.current = newZoom;
      wavesurferRef.current?.zoom(newZoom);
    },
    []
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!containerElement?.closest("dialog[open]")) return;
      if (isRecording) return;

      const tag = (e.target as HTMLElement).tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

      if (e.code === "Space") {
        e.preventDefault();
        handlePlayPause();
      } else if (e.code === "Delete" || e.code === "Backspace") {
        e.preventDefault();
        handleDeleteSelection();
      } else if (
        (e.metaKey || e.ctrlKey) &&
        e.shiftKey &&
        e.code === "KeyZ"
      ) {
        e.preventDefault();
        onRedo();
      } else if ((e.metaKey || e.ctrlKey) && e.code === "KeyZ") {
        e.preventDefault();
        onUndo();
      } else if (e.code === "Equal" || e.code === "NumpadAdd") {
        e.preventDefault();
        handleZoomIn();
      } else if (e.code === "Minus" || e.code === "NumpadSubtract") {
        e.preventDefault();
        handleZoomOut();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    handlePlayPause,
    handleDeleteSelection,
    onUndo,
    onRedo,
    handleZoomIn,
    handleZoomOut,
    isRecording,
    containerElement,
  ]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const hasAudio = !!audioData;

  return (
    <div className="ap-editor-toolbar flex items-center gap-1 px-3 py-2">
      {/* Transport controls */}
      <div className="flex items-center gap-0.5">
        <IconButton
          onClick={handleGoToBeginning}
          disabled={!hasAudio || isRecording}
          description="Go to Beginning"
        >
          <BackwardIcon className="size-3.5 text-slate-500 dark:text-base-content/60" />
        </IconButton>
        <IconButton
          onClick={handlePlayPause}
          disabled={!hasAudio || isRecording}
          description={isPlaying ? "Pause (Space)" : "Play (Space)"}
        >
          {isPlaying ? (
            <PauseIcon className="size-3.5 text-slate-500 dark:text-base-content/60" />
          ) : (
            <PlayIcon className="size-3.5 text-slate-500 dark:text-base-content/60" />
          )}
        </IconButton>
        <IconButton
          onClick={handleStop}
          disabled={!hasAudio || isRecording}
          description="Stop"
        >
          <StopIcon className="size-3.5 text-slate-500 dark:text-base-content/60" />
        </IconButton>
        <IconButton
          onClick={handleGoToEnd}
          disabled={!hasAudio || isRecording}
          description="Go to End"
        >
          <ForwardIcon className="size-3.5 text-slate-500 dark:text-base-content/60" />
        </IconButton>
      </div>

      <div className="w-px h-4 bg-base-300 mx-0.5" />

      {/* Edit controls */}
      <div className="flex items-center gap-0.5">
        <IconButton
          onClick={handleDeleteSelection}
          description="Delete Selection (Del)"
          disabled={!hasRegion || isRecording}
        >
          <TrashIcon className="size-3.5 text-red-500" />
        </IconButton>
        <IconButton
          onClick={onUndo}
          description="Undo (⌘Z)"
          disabled={!canUndo || isRecording}
        >
          <ArrowUturnLeftIcon className="size-3.5 text-slate-500 dark:text-base-content/60" />
        </IconButton>
        <IconButton
          onClick={onRedo}
          description="Redo (⌘⇧Z)"
          disabled={!canRedo || isRecording}
        >
          <ArrowUturnRightIcon className="size-3.5 text-slate-500 dark:text-base-content/60" />
        </IconButton>
      </div>

      <div className="w-px h-4 bg-base-300 mx-0.5" />

      {/* Zoom */}
      <div className="flex items-center gap-0.5">
        <IconButton
          onClick={handleZoomOut}
          disabled={!hasAudio}
          description="Zoom Out (-)"
        >
          <MagnifyingGlassMinusIcon className="size-3.5 text-slate-500 dark:text-base-content/60" />
        </IconButton>
        <input
          type="range"
          min={10}
          max={500}
          value={zoom}
          onChange={handleZoomSlider}
          className="ap-zoom-slider"
          disabled={!hasAudio}
        />
        <IconButton
          onClick={handleZoomIn}
          disabled={!hasAudio}
          description="Zoom In (+)"
        >
          <MagnifyingGlassPlusIcon className="size-3.5 text-slate-500 dark:text-base-content/60" />
        </IconButton>
      </div>

      {/* Time display */}
      <div className="ml-auto">
        <code className="text-[0.6875rem] text-slate-400 dark:text-base-content/40 tabular-nums">
          {formatTime(currentTime)} / {formatTime(duration)}
        </code>
      </div>
    </div>
  );
});

export default AudioWaveformEditor;

/** Minimal WAV encoder for loading into wavesurfer */
function createWavBuffer(
  channels: Float32Array[],
  sampleRate: number
): ArrayBuffer {
  const numChannels = channels.length;
  const numSamples = channels[0].length;
  const bytesPerSample = 2;
  const dataLength = numSamples * numChannels * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataLength);
  const view = new DataView(buffer);

  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++)
      view.setUint8(offset + i, str.charCodeAt(i));
  };

  writeStr(0, "RIFF");
  view.setUint32(4, 36 + dataLength, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * bytesPerSample, true);
  view.setUint16(32, numChannels * bytesPerSample, true);
  view.setUint16(34, bytesPerSample * 8, true);
  writeStr(36, "data");
  view.setUint32(40, dataLength, true);

  let offset = 44;
  for (let i = 0; i < numSamples; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const s = Math.max(-1, Math.min(1, channels[ch][i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      offset += 2;
    }
  }

  return buffer;
}
